import { TimesheetEstimatedTimeFragment } from 'generated/resources/graphql'

import AttendanceMinutesCalculationUtils from './AttendanceMinutesCalculationUtils'
import { AggregatedWorkedTimeBlocks } from './AttendanceWorkedTimeUtils'

export const calculateTimesheetBalanceBreakdown = ({
  aggregatedWorkedTime,
  estimatedTimes,
  extraHoursMinutes = 0,
  complementaryHoursMinutes,
}: {
  aggregatedWorkedTime?: AggregatedWorkedTimeBlocks | null
  estimatedTimes: TimesheetEstimatedTimeFragment[]
  extraHoursMinutes?: number
  complementaryHoursMinutes?: number
}) => {
  const today = new Date()

  const workedMinutes = aggregatedWorkedTime?.minutes ?? 0
  const paidBreaksMinutes = aggregatedWorkedTime?.paidBreaksMinutes ?? 0
  const effectiveWorkedMinutes = aggregatedWorkedTime?.effectiveWorkedMinutes ?? 0

  const estimatedRecords = estimatedTimes.filter((estimatedTime) => {
    const estimatedDate = new Date(estimatedTime.date)
    return estimatedDate <= today
  })
  const paidAbsencesMinutes = estimatedRecords.reduce(
    (acc, estimatedTime) => acc + (estimatedTime.absencesMinutes ?? 0),
    0
  )
  const estimatedMinutes = estimatedRecords.reduce(
    (acc, estimatedTime) => acc + (estimatedTime.minutes ?? 0),
    0
  )

  const contractMinutes = estimatedRecords.reduce(
    (acc, estimatedTime) => acc + (estimatedTime.contractMinutes ?? 0),
    0
  )

  const regularMinutes = aggregatedWorkedTime?.regularMinutes ?? 0
  const workedExtraMinutes = aggregatedWorkedTime?.extraTimeMinutes ?? 0
  const workedTimeBlocks = aggregatedWorkedTime?.workedTimeBlocks ?? []
  const approvedExtraTimeMinutes = aggregatedWorkedTime?.approvedExtraTimeMinutes ?? 0

  // If complementaryHoursMinutes is defined, it means we're in complementary hours mode
  // Calculate workedRegularMinutes from workedTimeBlocks to reflect local changes
  const isComplementaryHours = complementaryHoursMinutes !== undefined
  const workedRegularMinutes = isComplementaryHours
    ? workedTimeBlocks
        .filter((block) => block.timeType === 'regular' && block.workable)
        .reduce((sum, block) => sum + (block.minutes || 0), 0)
    : (aggregatedWorkedTime?.workedRegularMinutes ?? 0)

  const balancedRegularMinutes = isComplementaryHours
    ? workedRegularMinutes
    : AttendanceMinutesCalculationUtils.calculateBalancedRegularMinutes({
        regularMinutes,
        approvedWorkedExtraMinutes: approvedExtraTimeMinutes,
        extraHoursMinutesFromBalance: extraHoursMinutes,
      })
  const trackedMinutes = aggregatedWorkedTime?.trackedMinutes ?? 0

  const unapprovedOvertimeMinutes = workedTimeBlocks
    .filter((block) => block.timeType === 'overtime' && !block.approved)
    .reduce((sum, block) => sum + (block.minutes || 0), 0)

  const trackedMinutesDelta = aggregatedWorkedTime?.trackedMinutesDelta ?? 0

  return {
    workedMinutes,
    paidBreaksMinutes,
    paidAbsencesMinutes,
    effectiveWorkedMinutes,
    estimatedMinutes,
    contractMinutes,
    extraTimeMinutes: complementaryHoursMinutes ?? extraHoursMinutes,
    workedExtraMinutes,
    regularMinutes,
    balancedRegularMinutes,
    trackedMinutes,
    unapprovedOvertimeMinutes,
    trackedMinutesDelta,
  }
}
